/**
 * Типы домена воркспейсов (мультитенантность, бизнес-домен colver).
 *
 * Воркспейс = изолированное пространство одной компании-клиента (или её
 * подразделения). Пользователь может состоять в нескольких воркспейсах и
 * переключаться между ними (модель Notion).
 *
 * Доступы строятся от ДЕЙСТВИЙ, а не ролей: «может ли пользователь X выполнить
 * действие Y [над пользователем Z]». Участники образуют иерархию (parentSid) —
 * гранты со scope `subtree` распространяются на «пользователей подо мной».
 * Контракт повторяет backend `colver/core/*` (camelCase поля).
 */

import type {WorkspaceTheme} from '@lib/theme';

export type WorkspaceState = 'active' | 'suspended';
export type WorkspaceMembershipState = 'active' | 'invited' | 'suspended';

/** Краткое представление воркспейса текущего пользователя (для свитчера). */
export interface WorkspaceSummary {
  sid: string;
  name: string;
  slug: string;
  description?: string | null;
  state: WorkspaceState | string;
  membershipState: WorkspaceMembershipState | string;
  membershipSid: string;
}

/** Полный воркспейс (операторская консоль). */
export interface Workspace {
  sid: string;
  name: string;
  slug: string;
  description?: string | null;
  state: WorkspaceState | string;
  metadata?: Record<string, unknown>;
  /** UI-тема воркспейса (применяется ко всем участникам; null — дефолт фронта). */
  theme?: WorkspaceTheme | null;
  createdAt?: string;
  updatedAt?: string | null;
}

/** Необязательный справочник филиалов внутри Workspace. */
export interface WorkspaceBranch {
  sid: string;
  workspaceId: string;
  name: string;
  status: string;
  createdAt?: string;
  updatedAt?: string | null;
}

/** Тело создания воркспейса (оператор). */
export interface CreateWorkspaceInput {
  name: string;
  slug: string;
  description?: string | null;
  state?: WorkspaceState;
}

/** Тело изменения воркспейса. */
export interface UpdateWorkspaceInput {
  name?: string;
  slug?: string;
  description?: string | null;
  state?: WorkspaceState;
  /** Полная замена UI-темы воркспейса. */
  theme?: WorkspaceTheme | null;
  /**
   * Полная замена служебных метаданных (там же живут флаги модулей —
   * см. `lib/modules.ts`). Бэкенд перезаписывает поле целиком, поэтому слать
   * нужно весь объект, а не патч.
   */
  metadata?: Record<string, unknown>;
}

/**
 * Маркер идентичности участника: по такому признаку backend авто-атрибутирует
 * коммуникацию менеджеру (`metadata.userSid`). Например `{kind:'phone', value:'+7…'}`.
 */
export interface MemberIdentity {
  /** Тип маркера (kebab-case мнемоника: phone/extension/agent-id/email/sip). */
  kind: string;
  /** Значение маркера (телефон/добавочный/agent-id/email/sip). */
  value: string;
  /** Опциональная подпись для UI. */
  label?: string | null;
}

/** Участник воркспейса: членство (узел иерархии) + данные учётки. */
export interface WorkspaceMember {
  sid: string;
  workspaceId: string;
  userSid: string;
  /** Родительское членство в иерархии (null — корень). */
  parentSid?: string | null;
  /** Должность/подпись для UI (например «РОП»). */
  title?: string | null;
  /** Роль-пресет участника (см. реестр /roles; null — без роли). */
  role?: ColverRole | string | null;
  /** Назначение только РОПу; у менеджера филиал наследуется. */
  branchSid?: string | null;
  effectiveBranchSid?: string | null;
  effectiveBranchName?: string | null;
  /** Произвольные business-метаданные членства (например `callsPlan`). */
  metadata?: Record<string, unknown> | null;
  /** Маркеры идентичности для авто-атрибуции коммуникаций. */
  identities?: MemberIdentity[];
  /** Продвинутое MQL-правило матча коммуникации к участнику (null — нет). */
  matchRule?: Record<string, unknown> | null;
  state: WorkspaceMembershipState | string;
  displayName?: string | null;
  email?: string | null;
  userState?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

/** Учётка, заводимая вместе с членством (вместо userSid). */
export interface NewMemberUserInput {
  email: string;
  password?: string | null;
  displayName?: string | null;
}

/** Тело добавления участника: ровно один из userSid / newUser. */
export interface AddWorkspaceMemberInput {
  userSid?: string;
  newUser?: NewMemberUserInput;
  parentSid?: string | null;
  title?: string | null;
  role?: ColverRole | string | null;
  branchSid?: string | null;
  state?: WorkspaceMembershipState;
  /** Маркеры идентичности для авто-атрибуции коммуникаций. */
  identities?: MemberIdentity[];
  /** Продвинутое MQL-правило матча (null — без правила). */
  matchRule?: Record<string, unknown> | null;
}

/**
 * Тело изменения участника (поля опциональны; parentSid/role: null — сбросить).
 *
 * `identities` ПОЛНОСТЬЮ заменяет список (пустой массив — очистить).
 * `matchRule`: `null` — очистить, ОПУСКАТЬ ключ — оставить без изменений
 * (поэтому в типе он отдельно опционален: omission ≠ null).
 */
export interface UpdateWorkspaceMemberInput {
  parentSid?: string | null;
  title?: string | null;
  role?: ColverRole | string | null;
  branchSid?: string | null;
  state?: WorkspaceMembershipState;
  /** Заменяет весь список маркеров (`[]` — очистить). */
  identities?: MemberIdentity[];
  /** `null` — очистить правило; ключ опускается — не менять. */
  matchRule?: Record<string, unknown> | null;
}

// ─── Справочник маркеров идентичности (identity-fields) ──────────────────────

/** Вид маркера идентичности из справочника `GET .../members/identity-fields`. */
export interface IdentityKindDefinition {
  kind: string;
  title: string;
  description?: string | null;
  examples?: string[];
}

/** Оператор MQL для продвинутого matchRule (из identity-fields). */
export interface IdentityOperatorDefinition {
  operator: string;
  title: string;
  valueType?: string | null;
  description?: string | null;
  example?: string | null;
}

/** Наблюдаемый в коммуникациях сигнал (для автокомплита значений маркеров). */
export interface ObservedSignal {
  kind: string;
  value: string;
  count: number;
}

/** Ответ `GET /workspaces/{ws}/members/identity-fields`. */
export interface IdentityFieldsResponse {
  kinds: IdentityKindDefinition[];
  operators: IdentityOperatorDefinition[];
  observedSignals: ObservedSignal[];
  sampleSize: number;
}

// ─── Действия и гранты ────────────────────────────────────────────────────────

/** Мнемоники действий бизнес-домена (kebab-case, см. backend реестр). */
export type ColverAction =
  | 'view-communications'
  | 'view-analytics'
  | 'manage-members'
  | 'upload-communications'
  | 'delete-communications'
  | 'manage-access'
  | 'manage-plans'
  | 'manage-workspace';

/** Охват гранта: воркспейс целиком / поддерево иерархии / конкретный пользователь. */
export type AccessScope = 'workspace' | 'subtree' | 'user';

// ─── Роли-пресеты ─────────────────────────────────────────────────────────────

/**
 * Мнемоники ролей-пресетов воркспейса. Роль — пресет грантов на членстве,
 * материализуемый в обычные ActionGrant; enforcement остаётся action-based.
 */
export type ColverRole =
  | 'workspace-admin'
  | 'commercial-director'
  | 'sales-head'
  | 'manager';

/** Шаблон гранта в составе роли. */
export interface RoleGrantTemplate {
  action: ColverAction | string;
  scope: AccessScope | string;
}

/** Описание роли из реестра backend (GET /roles). */
export interface RoleDefinition {
  mnemonic: ColverRole | string;
  title: string;
  description: string;
  grants: RoleGrantTemplate[];
}

/** Описание действия из реестра backend. */
export interface ActionDefinition {
  mnemonic: ColverAction | string;
  title: string;
  description: string;
  /** Применяется к пользователям (иначе — к воркспейсу целиком). */
  targeted: boolean;
}

/** Выданное действие участнику. */
export interface GrantSummary {
  sid: string;
  workspaceId: string;
  userSid: string;
  action: ColverAction | string;
  scope: AccessScope | string;
  targetUserSid?: string | null;
  grantedBy?: string | null;
  createdAt?: string | null;
}

/** Тело выдачи действия. */
export interface GrantActionInput {
  userSid: string;
  action: ColverAction | string;
  scope?: AccessScope;
  targetUserSid?: string | null;
}

/** Решение проверки доступа (`can user do action [for user]`). */
export interface AccessDecision {
  allowed: boolean;
  reason?: string | null;
  hint?: string | null;
}

/** Сводка доступов текущего пользователя в воркспейсе (для гейтинга UI). */
export interface MyAccess {
  workspaceSid: string;
  userSid: string;
  /** Роль членства из access/my; null не означает менеджера или администратора. */
  role: ColverRole | null;
  isOperator: boolean;
  membershipSid?: string | null;
  membershipState?: string | null;
  grants: GrantSummary[];
}

/** Человекочитаемые подписи охватов грантов. */
export const ACCESS_SCOPE_LABELS: Record<string, string> = {
  workspace: 'Весь воркспейс',
  subtree: 'Пользователи подо мной',
  user: 'Конкретный пользователь',
};

/** Есть ли у сводки доступов действие (любым охватом). */
export const hasAction = (
  access: MyAccess | undefined,
  action: string,
): boolean =>
  Boolean(
    access &&
    (access.isOperator || access.grants.some((g) => g.action === action)),
  );
